import { useCreateLocation, useUpdateLocation, useGetLocations } from "api/locations/service";
import React, { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Card,
    CardHeader,
    CardBody,
    CardTitle,
    Row,
    Col,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Spinner,
    CustomInput,
} from "reactstrap";
import { getActiveColorStyle } from "../../../utils/colorUtils";

const LocationAdd = ({ activeColor = "secondary" }) => {
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const navigate = useNavigate();
    const createLocationMutation = useCreateLocation();
    const updateLocationMutation = useUpdateLocation();
    const { data: locationsData } = useGetLocations(undefined, { enabled: isEditMode });

    const [formData, setFormData] = useState({
        documentId: '',
        location_name: '',
        location_description: '',
        lat: '',
        long: '',
        active: true,
    });

    const [errors, setErrors] = useState({});

    React.useEffect(() => {
        if (isEditMode && locationsData && locationsData.results) {
            const loc = locationsData.results.find(l => String(l.id) === String(id));
            if (loc) {
                setFormData({
                    documentId: loc.documentId,
                    location_name: loc.location_name || '',
                    location_description: loc.location_description || '',
                    lat: loc.lat || '',
                    long: loc.long || '',
                    active: loc.location_status === 1,
                });
            }
        }
    }, [isEditMode, locationsData, id]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.location_name.trim()) {
            newErrors.location_name = 'Location name is required';
        }

        if (!formData.lat.trim()) {
            newErrors.lat = 'Latitude is required';
        } else if (isNaN(formData.lat) || parseFloat(formData.lat) < -90 || parseFloat(formData.lat) > 90) {
            newErrors.lat = 'Latitude must be a valid number between -90 and 90';
        }

        if (!formData.long.trim()) {
            newErrors.long = 'Longitude is required';
        } else if (isNaN(formData.long) || parseFloat(formData.long) < -180 || parseFloat(formData.long) > 180) {
            newErrors.long = 'Longitude must be a valid number between -180 and 180';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };



    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        if (isEditMode) {
            updateLocationMutation.mutate({
                id: formData.documentId,
                data: {
                    location_name: formData.location_name,
                    location_description: formData.location_description,
                    location_status: formData.active ? 1 : 0,
                    lat: formData.lat,
                    long: formData.long,
                }
            });
        } else {
            createLocationMutation.mutate({
                location_name: formData.location_name,
                location_description: formData.location_description,
                location_status: formData.active ? 1 : 0,
                lat: formData.lat,
                long: formData.long,
            });
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    return (
        <div className="content">
            <Row>
                <Col md="12">
                    <Card>
                        <CardHeader>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <CardTitle tag="h4" style={{ margin: 0 }}>
                                    <i className="tim-icons icon-pin" style={{ marginRight: '10px' }} />
                                    {isEditMode ? 'Edit Location' : 'Add New Location'}
                                </CardTitle>
                                <Button
                                    color="secondary"
                                    size="sm"
                                    onClick={handleCancel}
                                    disabled={createLocationMutation.isPending || updateLocationMutation.isPending}
                                >
                                    <i className="tim-icons icon-simple-remove" style={{ marginRight: '5px' }} />
                                    Cancel
                                </Button>
                            </div>
                        </CardHeader>
                        <CardBody>
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for="location_name" className="form-control-label">
                                                Location Name <span style={{ color: 'red' }}>*</span>
                                            </Label>
                                            <Input
                                                id="location_name"
                                                name="location_name"
                                                type="text"
                                                value={formData.location_name}
                                                onChange={handleInputChange}
                                                className={errors.location_name ? 'is-invalid' : ''}
                                                placeholder="Enter location name"
                                                disabled={createLocationMutation.isPending || updateLocationMutation.isPending}
                                            />
                                            {errors.location_name && (
                                                <div className="invalid-feedback d-block">
                                                    {errors.location_name}
                                                </div>
                                            )}
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className="form-control-label">
                                                Status
                                            </Label>
                                            <div className="d-flex align-items-center" style={{ height: '38px' }}>
                                                <CustomInput
                                                    type="switch"
                                                    id="active"
                                                    name="active"
                                                    label={formData.active ? "Active" : "Inactive"}
                                                    checked={formData.active}
                                                    onChange={handleInputChange}
                                                    disabled={createLocationMutation.isPending || updateLocationMutation.isPending}
                                                />
                                            </div>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md="12">
                                        <FormGroup>
                                            <Label for="location_description" className="form-control-label">
                                                Description
                                            </Label>
                                            <Input
                                                id="location_description"
                                                name="location_description"
                                                type="textarea"
                                                rows="3"
                                                value={formData.location_description}
                                                onChange={handleInputChange}
                                                className={errors.location_description ? 'is-invalid' : ''}
                                                placeholder="Enter location description"
                                                disabled={createLocationMutation.isPending || updateLocationMutation.isPending}
                                            />
                                            {errors.location_description && (
                                                <div className="invalid-feedback d-block">
                                                    {errors.location_description}
                                                </div>
                                            )}
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for="lat" className="form-control-label">
                                                Latitude <span style={{ color: 'red' }}>*</span>
                                            </Label>
                                            <Input
                                                id="lat"
                                                name="lat"
                                                type="number"
                                                step="any"
                                                value={formData.lat}
                                                onChange={handleInputChange}
                                                className={errors.lat ? 'is-invalid' : ''}
                                                placeholder="e.g., 40.7128"
                                                disabled={createLocationMutation.isPending || updateLocationMutation.isPending}
                                            />
                                            {errors.lat && (
                                                <div className="invalid-feedback d-block">
                                                    {errors.lat}
                                                </div>
                                            )}
                                            <small className="form-text text-muted">
                                                Enter latitude between -90 and 90 degrees
                                            </small>
                                        </FormGroup>
                                    </Col>

                                    <Col md="6">
                                        <FormGroup>
                                            <Label for="long" className="form-control-label">
                                                Longitude <span style={{ color: 'red' }}>*</span>
                                            </Label>
                                            <Input
                                                id="long"
                                                name="long"
                                                type="number"
                                                step="any"
                                                value={formData.long}
                                                onChange={handleInputChange}
                                                className={errors.long ? 'is-invalid' : ''}
                                                placeholder="e.g., -74.0060"
                                                disabled={createLocationMutation.isPending || updateLocationMutation.isPending}
                                            />
                                            {errors.long && (
                                                <div className="invalid-feedback d-block">
                                                    {errors.long}
                                                </div>
                                            )}
                                            <small className="form-text text-muted">
                                                Enter longitude between -180 and 180 degrees
                                            </small>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md="12">
                                        <div className="d-flex justify-content-end gap-2 mt-4">
                                            <Button
                                                color="secondary"
                                                onClick={handleCancel}
                                                disabled={createLocationMutation.isPending || updateLocationMutation.isPending}
                                                className="px-4"
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                style={{ ...activeColorStyle, color: 'white' }}
                                                type="submit"
                                                disabled={createLocationMutation.isPending || updateLocationMutation.isPending}
                                                className="px-4"
                                            >
                                                {createLocationMutation.isPending || updateLocationMutation.isPending ? (
                                                    <>
                                                        <Spinner size="sm" className="me-2" />
                                                        {isEditMode ? 'Updating...' : 'Creating...'}
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className={isEditMode ? "tim-icons icon-pencil" : "tim-icons icon-simple-add"} style={{ marginRight: '8px' }} />
                                                        {isEditMode ? 'Update Location' : 'Create Location'}
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default LocationAdd; 